'use client';

import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, RotateCcw, Download } from 'lucide-react';

const FIB = [0, 1, 2, 3, 5, 8, 13, 21, '?'] as const;
type Point = (typeof FIB)[number];

type Story = {
  id: string;
  title: string;
  estimate: Point;
  reference?: boolean;
};

const STORAGE_KEY = 'planetroadmap.story-point-estimator.v1';

function newStory(title = '', estimate: Point = '?', reference = false): Story {
  return {
    id: Math.random().toString(36).slice(2),
    title,
    estimate,
    reference,
  };
}

function defaultStories(): Story[] {
  return [
    newStory('Add a "remember me" checkbox to login', 1, true),
    newStory('Build a new admin settings page', 5, true),
    newStory('Migrate to a new auth provider (full project)', 13, true),
    newStory('', '?'),
    newStory('', '?'),
    newStory('', '?'),
  ];
}

const POINT_GUIDE: Record<string, string> = {
  '0': 'Free — already done or no work',
  '1': 'Tiny — under an hour, well-understood',
  '2': 'Small — a few hours, simple change',
  '3': 'Medium — half a day, some unknowns',
  '5': 'Large — a full day or two, real complexity',
  '8': 'Very large — multi-day, multiple unknowns',
  '13': 'Huge — should be split into smaller stories',
  '21': 'Epic — definitely split',
  '?': 'Unknown — need more info before estimating',
};

export function StoryPointEstimator() {
  const [stories, setStories] = useState<Story[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Story[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setStories(parsed);
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setStories(defaultStories());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stories));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [stories, hydrated]);

  function update(id: string, patch: Partial<Story>) {
    setStories((curr) => curr.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }

  function remove(id: string) {
    setStories((curr) => curr.filter((s) => s.id !== id));
  }

  function add() {
    setStories((curr) => [...curr, newStory()]);
  }

  function reset() {
    if (!confirm('Reset to default examples?')) return;
    setStories(defaultStories());
  }

  function exportCsv() {
    const rows = [
      ['Title', 'Estimate', 'Reference?'],
      ...stories.map((s) => [s.title || 'Untitled', s.estimate, s.reference ? 'Yes' : '']),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'story-points.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const stats = useMemo(() => {
    const numeric: number[] = [];
    for (const s of stories) {
      if (typeof s.estimate === 'number') numeric.push(s.estimate);
    }
    const total = numeric.reduce((a, b) => a + b, 0);
    const median = numeric.length
      ? [...numeric].sort((a, b) => a - b)[Math.floor(numeric.length / 2)]
      : 0;
    const unknown = stories.filter((s) => s.estimate === '?').length;
    const tooBig = stories.filter((s) => typeof s.estimate === 'number' && s.estimate >= 13).length;
    return { total, median, unknown, tooBig, count: numeric.length };
  }, [stories]);

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading estimator…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total points" value={String(stats.total)} accent />
        <StatCard label="Stories estimated" value={String(stats.count)} />
        <StatCard label="Need refinement" value={String(stats.unknown)} warn={stats.unknown > 0} />
        <StatCard label="Too big (≥13)" value={String(stats.tooBig)} warn={stats.tooBig > 0} />
      </div>

      {stats.tooBig > 0 && (
        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-2xl p-4 text-sm">
          <span className="font-semibold text-amber-700 dark:text-amber-400">
            {stats.tooBig} {stats.tooBig === 1 ? 'story is' : 'stories are'} 13+ points.
          </span>{' '}
          <span className="text-muted-foreground">
            Stories that big are usually epics in disguise — split them before sprint planning.
          </span>
        </div>
      )}

      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/30">
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground w-8">#</th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground min-w-[280px]">
                  Story
                </th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">Estimate</th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">Guidance</th>
                <th className="text-center py-3 px-4 font-semibold text-muted-foreground w-20">
                  Reference
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {stories.map((s, i) => (
                <tr key={s.id} className={i < stories.length - 1 ? 'border-b border-border/40' : ''}>
                  <td className="py-2 px-4 text-muted-foreground font-mono text-xs">{i + 1}</td>
                  <td className="py-2 px-4">
                    <input
                      type="text"
                      value={s.title}
                      onChange={(e) => update(s.id, { title: e.target.value })}
                      placeholder="Story title or description"
                      className="w-full bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <div className="flex flex-wrap gap-1">
                      {FIB.map((p) => (
                        <button
                          key={String(p)}
                          onClick={() => update(s.id, { estimate: p })}
                          className={`min-w-[28px] h-7 rounded text-xs font-mono font-semibold transition-colors ${
                            s.estimate === p
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 px-4 text-xs text-muted-foreground">
                    {POINT_GUIDE[String(s.estimate)] || ''}
                  </td>
                  <td className="py-2 px-4 text-center">
                    <input
                      type="checkbox"
                      checked={!!s.reference}
                      onChange={(e) => update(s.id, { reference: e.target.checked })}
                      className="accent-primary"
                      aria-label="Use as reference"
                    />
                  </td>
                  <td className="py-2 px-2">
                    <button
                      onClick={() => remove(s.id)}
                      aria-label="Remove story"
                      className="text-muted-foreground hover:text-red-500 p-1 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {stories.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-muted-foreground">
                    No stories yet. Add one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <button
          onClick={add}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all"
        >
          <Plus className="w-4 h-4" />
          Add story
        </button>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={stories.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center pt-2">
        Saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}

function StatCard({
  label,
  value,
  accent,
  warn,
}: {
  label: string;
  value: string;
  accent?: boolean;
  warn?: boolean;
}) {
  let cls = 'bg-white dark:bg-card border border-border/60';
  if (accent && !warn) cls = 'bg-primary/5 border border-primary/20';
  if (warn) cls = 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40';
  return (
    <div className={`rounded-2xl p-4 ${cls}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={`text-2xl font-bold mt-1 ${
          accent && !warn ? 'text-primary' : ''
        } ${warn ? 'text-amber-700 dark:text-amber-400' : ''}`}
      >
        {value}
      </div>
    </div>
  );
}
